import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Table, Input, Select, Tooltip, Space } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  SecondaryButton,
  CancelButton,
} from "components/Common/CustomButtons/CustomButtons";
import {
  TRADE_COLUMNS,
  VALID_TYPES,
  getTradeCaptureCellValidationMessages,
} from "constants/tradeCapture";
import {
  downloadTradeCaptureTableAsCsv,
  ensureActionedAtTimeOnRows,
} from "helpers/tradeCaptureHelper";

const buildEmptyRow = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.key] = "";
    return acc;
  }, {});

const isLiveRow = (live) => live === 1 || live === "1" || live === true;

const EditableCell = ({
  editing,
  dataIndex,
  record,
  children,
  onSave,
  onStartEdit,
  isDisplayOnly,
  isLiveUl,
  errorMessages,
  placeholder,
  ...restProps
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState("");

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (editing) {
      setLocalValue(record[dataIndex] ?? "");
    }
  }, [editing, record, dataIndex]);

  const handleSave = () => {
    onSave(dataIndex, localValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
    if (e.key === "Escape") {
      onSave(dataIndex, record[dataIndex]);
    }
  };

  const cellClasses = [];
  if (isDisplayOnly) cellClasses.push("trade-capture-table__cell--display-only");
  if (isLiveUl) cellClasses.push("trade-capture-table__cell--live-ul");
  if (errorMessages?.length) cellClasses.push("trade-capture-table__cell--error");

  let cellContent;

  if (editing) {
    if (dataIndex === "type") {
      cellContent = (
        <Select
          ref={inputRef}
          value={localValue}
          onChange={(val) => {
            setLocalValue(val);
            onSave(dataIndex, val);
          }}
          onBlur={handleSave}
          size="small"
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="children"
          autoFocus
          aria-label={restProps["aria-label"]}
        >
          {VALID_TYPES.map((t) => (
            <Select.Option key={t} value={t}>
              {t}
            </Select.Option>
          ))}
        </Select>
      );
    } else {
      cellContent = (
        <Input
          ref={inputRef}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          size="small"
          placeholder={placeholder}
          aria-label={restProps["aria-label"]}
        />
      );
    }
  } else {
      cellContent = onStartEdit ? (
      <div
        className="trade-capture-table__cell-value"
        onClick={onStartEdit}
        role="button"
        tabIndex={0}
        aria-label={restProps["aria-label"]}
        onKeyDown={(e) => {
          if (e.key === "Enter") onStartEdit();
        }}
      >
        {children}
      </div>
    ) : (
      <div className="trade-capture-table__cell-readonly">{children}</div>
    );
  }

  const wrappedContent = errorMessages?.length ? (
    <Tooltip
      title={errorMessages.join("; ")}
      color="#ff4d4f"
      overlayStyle={{ maxWidth: 400 }}
    >
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  );

  const tdClassName = [restProps.className, ...cellClasses]
    .filter(Boolean)
    .join(" ");

  return (
    <td {...restProps} className={tdClassName}>
      {wrappedContent}
    </td>
  );
};

/**
 * Editable screenshot-import grid. Defaults to the Option Capture trade
 * columns. The Other surface ladder passes its own `columns`, a
 * `normalizeRows` that leaves its rows as they are, and `deriveDisplayRows`
 * to show C/P and premium from the UL without storing them as edits.
 */
const TradeTable = ({
  tableData,
  validationErrors,
  onTableUpdate,
  disabled,
  sessionId,
  columns: columnConfig = TRADE_COLUMNS,
  normalizeRows = ensureActionedAtTimeOnRows,
  deriveDisplayRows,
}) => {
  const [localTableData, setLocalTableData] = useState(() =>
    normalizeRows(tableData)
  );
  const normalizeRowsRef = useRef(normalizeRows);
  normalizeRowsRef.current = normalizeRows;
  const [editingCell, setEditingCell] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const onTableUpdateRef = useRef(onTableUpdate);
  onTableUpdateRef.current = onTableUpdate;

  useEffect(() => {
    setLocalTableData(normalizeRowsRef.current(tableData));
  }, [tableData]);

  const displayRows = useMemo(
    () =>
      deriveDisplayRows ? deriveDisplayRows(localTableData) : localTableData,
    [deriveDisplayRows, localTableData]
  );

  const getErrorsForCell = useCallback(
    (rowIndex, field) =>
      getTradeCaptureCellValidationMessages(validationErrors, rowIndex, field, {
        option: localTableData?.[rowIndex]?.option,
      }),
    [validationErrors, localTableData]
  );

  const handleCellSave = useCallback((rowIndex, field, value) => {
    setEditingCell(null);
    setLocalTableData((prev) => {
      const newData = normalizeRowsRef.current(
        prev.map((row, idx) => {
          if (idx !== rowIndex) return row;
          return { ...row, [field]: value };
        })
      );
      if (JSON.stringify(newData[rowIndex]) !== JSON.stringify(prev[rowIndex])) {
        onTableUpdateRef.current?.(newData);
      }
      return newData;
    });
  }, []);

  const handleStartEdit = useCallback(
    (rowIndex, field) => {
      if (disabled) return;
      setEditingCell({ rowIndex, field });
    },
    [disabled]
  );

  const columns = columnConfig.map((col) => ({
    title: col.title,
    dataIndex: col.key,
    key: col.key,
    width: col.width,
    ellipsis: true,
    onCell: (record, rowIndex) => ({
      record,
      dataIndex: col.key,
      editing:
        editingCell?.rowIndex === rowIndex && editingCell?.field === col.key,
      onSave: (field, value) => handleCellSave(rowIndex, field, value),
      onStartEdit: col.editable
        ? () => handleStartEdit(rowIndex, col.key)
        : undefined,
      isDisplayOnly: col.displayOnly,
      isLiveUl: col.key === "ul" && isLiveRow(record.live),
      errorMessages: getErrorsForCell(rowIndex, col.key),
      placeholder: col.placeholder,
      "aria-label": `${col.title}, row ${rowIndex + 1}`,
    }),
  }));

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const handleDelete = () => {
    if (!selectedRowKeys.length) return;
    const newData = localTableData.filter((_, idx) => !selectedRowKeys.includes(idx));
    setLocalTableData(newData);
    setSelectedRowKeys([]);
    onTableUpdateRef.current?.(newData);
  };

  const handleAddRowClick = () => {
    const newData = [...localTableData, buildEmptyRow(columnConfig)];
    setLocalTableData(newData);
    onTableUpdateRef.current?.(newData);
  };

  const handleDownloadCsv = () => {
    downloadTradeCaptureTableAsCsv(displayRows, {
      sessionId,
      columns: columnConfig,
    });
  };

  return (
    <div className="trade-capture-table">
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        dataSource={displayRows}
        columns={columns}
        rowSelection={rowSelection}
        rowKey={(_, index) => index}
        pagination={false}
        scroll={{ x: "max-content", y: 480 }}
        size="small"
        bordered
      />
      <Space className="trade-capture-table__actions" style={{ marginTop: 8 }}>
        <SecondaryButton
          icon={<PlusOutlined />}
          onClick={handleAddRowClick}
          disabled={disabled}
          size="small"
        >
          Add Row
        </SecondaryButton>
        <CancelButton
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          disabled={disabled || !selectedRowKeys.length}
          size="small"
        >
          Delete Selected ({selectedRowKeys.length})
        </CancelButton>
        <SecondaryButton
          icon={<DownloadOutlined />}
          onClick={handleDownloadCsv}
          disabled={!localTableData?.length}
          size="small"
        >
          Download CSV
        </SecondaryButton>
      </Space>
    </div>
  );
};

export default TradeTable;
